import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatDistanceToNow } from 'date-fns';
import { pt } from 'date-fns/locale';
import './GamificationPage.css';

// `userType` existe para o menu lateral não mentir: em /admin/gamification a página tem de aparecer com o
// menu de administração (antes abria sempre com o menu do atleta, dentro da área de gestão).
const GamificationPage = ({ userType = 'client' }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('weekly');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'leaderboard') {
      loadLeaderboard();
    }
  }, [leaderboardPeriod, activeTab]);

  const loadData = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      // Sem `catch` por chamada: as três são o mesmo ecrã e, em falha, o atleta tem de saber que
      // falhou. Com um valor de recurso por chamada, um erro de rede mostrava «Nível 1 · 0 XP» a
      // quem tem 3000 XP — parecia uma conta nova, e o `catch` de baixo nunca chegava a correr.
      const [statsRes, badgesRes, challengesRes] = await Promise.all([
        api.get('/gamification/stats'),
        api.get('/gamification/badges'),
        api.get('/gamification/challenges')
      ]);
      setStats(statsRes.data);
      setBadges(badgesRes.data || []);
      setChallenges(challengesRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setStats(null);
      setBadges([]);
      setChallenges([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboard = async () => {
    try {
      const res = await api.get(`/gamification/leaderboard?period=${leaderboardPeriod}`);
      setLeaderboard(res.data || []);
      setLeaderboardError(false);
    } catch (error) {
      console.error('Erro ao carregar leaderboard:', error);
      setLeaderboard([]);
      setLeaderboardError(true);
    }
  };

  const handleJoinChallenge = async (challengeId) => {
    try {
      await api.post(`/gamification/challenges/${challengeId}/join`);
      toast.success('Inscrito no desafio com sucesso! 🎯');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao entrar no desafio');
    }
  };

  const getXpProgress = () => {
    if (!stats) return 0;
    return Math.min((stats.xpProgress / 100) * 100, 100);
  };

  // Ensure all data arrays are valid before using array methods
  const badgesArray = Array.isArray(badges) ? badges : [];
  const challengesArray = Array.isArray(challenges) ? challenges : [];
  const leaderboardArray = Array.isArray(leaderboard) ? leaderboard : [];
  
  const earnedBadges = badgesArray.filter(b => b.earned);
  const lockedBadges = badgesArray.filter(b => !b.earned);
  const activeChallenges = challengesArray.filter(c => c.isJoined && !c.isCompleted);

  if (loading) {
    return (
      <DashboardLayout userType={userType}>
        <div className="loading-container"><div className="spinner"></div></div>
      </DashboardLayout>
    );
  }

  if (loadError) {
    // Sem números de recurso: um erro de rede não é «Nível 1 · 0 XP».
    return (
      <DashboardLayout userType={userType}>
        <div className="gamification-page">
          <div className="empty-state" style={{ padding: '60px 20px', textAlign: 'center' }} role="alert">
            <div style={{ fontSize: 56, marginBottom: 12 }}>⚠️</div>
            <h3 style={{ marginBottom: 8 }}>Não foi possível carregar as conquistas</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>A ligação falhou. Os seus pontos e medalhas continuam guardados.</p>
            <button type="button" className="btn-secondary" onClick={loadData}>Tentar de novo</button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType={userType}>
      <div className="gamification-page">
        {/* Level Card */}
        <div className="level-card">
          <div className="level-header">
            <div className="level-info">
              <h3>O SEU NÍVEL</h3>
              <div className="level-number">{stats?.level || 1}</div>
            </div>
            <div className="level-xp">
              <div className="current">{stats?.totalXp || 0} XP</div>
              <div className="next">Próximo nível: {stats?.xpForNextLevel || 100} XP</div>
            </div>
          </div>
          <div className="xp-progress-bar">
            <div className="xp-progress-fill" style={{ width: `${getXpProgress()}%` }}></div>
          </div>
          <div className="level-stats">
            <div className="level-stat">
              <div className="level-stat-value">🔥 {stats?.currentStreak || 0}</div>
              <div className="level-stat-label">Dias seguidos</div>
            </div>
            <div className="level-stat">
              <div className="level-stat-value">🏆 #{stats?.ranking || '-'}</div>
              <div className="level-stat-label">Ranking</div>
            </div>
            <div className="level-stat">
              <div className="level-stat-value">🎖️ {stats?.badgesCount || 0}</div>
              <div className="level-stat-label">Badges</div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">🏋️</div>
            <div className="stat-value">{stats?.totalWorkouts || 0}</div>
            <div className="stat-label">Treinos</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-value">{stats?.totalBookings || 0}</div>
            <div className="stat-label">Reservas</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⏱️</div>
            <div className="stat-value">{stats?.totalMinutes || 0}</div>
            <div className="stat-label">Minutos</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🔥</div>
            <div className="stat-value">{stats?.totalCalories || 0}</div>
            <div className="stat-label">Calorias</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="gamification-tabs">
          <button 
            className={`gami-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📊 Visão Geral
          </button>
          <button 
            className={`gami-tab ${activeTab === 'badges' ? 'active' : ''}`}
            onClick={() => setActiveTab('badges')}
          >
            🎖️ Badges ({earnedBadges.length}/{badges.length})
          </button>
          <button 
            className={`gami-tab ${activeTab === 'challenges' ? 'active' : ''}`}
            onClick={() => setActiveTab('challenges')}
          >
            🎯 Desafios ({challenges.length})
          </button>
          <button 
            className={`gami-tab ${activeTab === 'leaderboard' ? 'active' : ''}`}
            onClick={() => { setActiveTab('leaderboard'); loadLeaderboard(); }}
          >
            🏆 Ranking
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="overview-content">
            <div className="data-card">
              <div className="card-header">
                <h3 className="card-title">🎖️ Últimas Conquistas</h3>
              </div>
              {earnedBadges.length > 0 ? (
                <div className="badges-grid">
                  {earnedBadges.slice(0, 6).map((badge) => (
                    <div key={badge.id} className="badge-card earned">
                      <div className="badge-earned-mark">✓</div>
                      <div className="badge-icon">{badge.icon || '🏅'}</div>
                      <div className="badge-name">{badge.name}</div>
                      <div className="badge-desc">{badge.description}</div>
                      <div className="badge-xp">+{badge.xpReward} XP</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-challenges">
                  <div className="empty-icon">🎖️</div>
                  <h3>Ainda sem badges</h3>
                  <p>Complete desafios e treinos para ganhar badges!</p>
                </div>
              )}
            </div>

            <div className="data-card" style={{ marginTop: '20px' }}>
              <div className="card-header">
                <h3 className="card-title">🎯 Desafios Ativos</h3>
              </div>
              {activeChallenges.length > 0 ? (
                <div className="challenges-list">
                  {activeChallenges.slice(0, 3).map((challenge) => (
                    <ChallengeCard 
                      key={challenge.id} 
                      challenge={challenge} 
                      onJoin={handleJoinChallenge}
                    />
                  ))}
                </div>
              ) : (
                <div className="empty-challenges">
                  <div className="empty-icon">🎯</div>
                  <h3>Sem desafios ativos</h3>
                  <p>Vá à aba Desafios para se inscrever!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Badges Tab */}
        {activeTab === 'badges' && (
          <div className="data-card">
            <div className="card-header">
              <h3 className="card-title">Todos os Badges</h3>
            </div>
            
            {earnedBadges.length > 0 && (
              <>
                <h4 style={{ margin: '0 0 16px', color: 'var(--text-primary)' }}>
                  ✅ Conquistados ({earnedBadges.length})
                </h4>
                <div className="badges-grid" style={{ marginBottom: '32px' }}>
                  {earnedBadges.map((badge) => (
                    <div key={badge.id} className="badge-card earned">
                      <div className="badge-earned-mark">✓</div>
                      <div className="badge-icon">{badge.icon || '🏅'}</div>
                      <div className="badge-name">{badge.name}</div>
                      <div className="badge-desc">{badge.description}</div>
                      <div className="badge-xp">+{badge.xpReward} XP</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {lockedBadges.length > 0 && (
              <>
                <h4 style={{ margin: '0 0 16px', color: 'var(--text-secondary)' }}>
                  🔒 Por Conquistar ({lockedBadges.length})
                </h4>
                <div className="badges-grid">
                  {lockedBadges.map((badge) => (
                    <div key={badge.id} className="badge-card locked">
                      <div className="badge-icon">{badge.icon || '🏅'}</div>
                      <div className="badge-name">{badge.name}</div>
                      <div className="badge-desc">{badge.description}</div>
                      <div className="badge-xp">+{badge.xpReward} XP</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {badges.length === 0 && (
              <div className="empty-challenges">
                <div className="empty-icon">🎖️</div>
                <h3>Sem badges disponíveis</h3>
                <p>Os badges serão adicionados em breve!</p>
              </div>
            )}
          </div>
        )}

        {/* Challenges Tab */}
        {activeTab === 'challenges' && (
          <div className="data-card">
            <div className="card-header">
              <h3 className="card-title">Desafios Disponíveis</h3>
            </div>
            
            {challengesArray.length > 0 ? (
              <div className="challenges-list">
                {challengesArray.map((challenge) => (
                  <ChallengeCard 
                    key={challenge.id} 
                    challenge={challenge} 
                    onJoin={handleJoinChallenge}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-challenges">
                <div className="empty-icon">🎯</div>
                <h3>Sem desafios ativos</h3>
                <p>Novos desafios serão publicados em breve!</p>
              </div>
            )}
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="data-card">
            <div className="leaderboard-header">
              <h3 className="card-title">🏆 Ranking</h3>
              <div className="period-toggle">
                <button 
                  className={`period-btn ${leaderboardPeriod === 'weekly' ? 'active' : ''}`}
                  onClick={() => setLeaderboardPeriod('weekly')}
                >
                  Semanal
                </button>
                <button 
                  className={`period-btn ${leaderboardPeriod === 'monthly' ? 'active' : ''}`}
                  onClick={() => setLeaderboardPeriod('monthly')}
                >
                  Mensal
                </button>
                <button 
                  className={`period-btn ${leaderboardPeriod === 'alltime' ? 'active' : ''}`}
                  onClick={() => setLeaderboardPeriod('alltime')}
                >
                  Total
                </button>
              </div>
            </div>
            
            {leaderboardError ? (
              <div className="empty-state" role="alert">
                <p>Não foi possível carregar a tabela de classificação. <button type="button" className="btn-secondary" onClick={loadLeaderboard}>Tentar de novo</button></p>
              </div>
            ) : leaderboardArray.length > 0 ? (
              <div className="leaderboard-list">
                {leaderboardArray.map((entry, index) => (
                  <div 
                    key={index} 
                    className={`leaderboard-item ${index === 0 ? 'top-1' : ''} ${index === 1 ? 'top-2' : ''} ${index === 2 ? 'top-3' : ''}`}
                  >
                    <div className="rank">
                      {index === 0 ? <span className="rank-medal">🥇</span> :
                       index === 1 ? <span className="rank-medal">🥈</span> :
                       index === 2 ? <span className="rank-medal">🥉</span> :
                       `#${index + 1}`}
                    </div>
                    <div className="user-info">
                      <div className="user-name">{entry.userName || 'Anónimo'}</div>
                      <div className="user-workouts">{entry.workoutsCompleted || 0} treinos</div>
                    </div>
                    <div className="user-points">{entry.points || 0} pts</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-challenges">
                <div className="empty-icon">🏆</div>
                <h3>Ranking vazio</h3>
                <p>Seja o primeiro a entrar no ranking!</p>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Challenge Card Component
const ChallengeCard = ({ challenge, onJoin }) => {
  const progress = challenge.isJoined ? 
    Math.min((challenge.progress / challenge.targetValue) * 100, 100) : 0;

  const getTimeLeft = () => {
    if (!challenge.endDate) return '';
    try {
      return formatDistanceToNow(new Date(challenge.endDate), { locale: pt, addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <div className="challenge-card">
      <div className="challenge-icon">{challenge.icon || '🎯'}</div>
      <div className="challenge-content">
        <div className="challenge-header">
          <h4 className="challenge-title">{challenge.title}</h4>
          <span className="challenge-reward">+{challenge.xpReward} XP</span>
        </div>
        <p className="challenge-desc">{challenge.description}</p>
        
        {challenge.isJoined && (
          <div className="challenge-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <div className="progress-text">
              {challenge.progress || 0} / {challenge.targetValue} ({Math.round(progress)}%)
            </div>
          </div>
        )}
        
        <div className="challenge-footer">
          <span className="challenge-time">⏰ Termina {getTimeLeft()}</span>
          <span className="challenge-participants">
            👥 {challenge.participantCount || 0} participantes
          </span>
          {challenge.isCompleted ? (
            <button className="join-btn completed" disabled>✅ Completado</button>
          ) : challenge.isJoined ? (
            <button className="join-btn joined" disabled>📍 Inscrito</button>
          ) : (
            <button className="join-btn" onClick={() => onJoin(challenge.id)}>
              Participar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GamificationPage;
